/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * @file WatsonxOrchestrateSelfAssessment.tsx
 * Exports a top-level React component that handles routing, context, and i18n.
 */
import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import './WatsonxOrchestrateSelfAssessment.scss';
import 'src/common/style/common.scss';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { UT30WatsonxOSA } from '../constants/default';
import Welcome from './Templates/Welcome/Welcome';
import Assessment from './Templates/Assessment/Assessment';
import { Landing } from './Molecules/Landing/Landing';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { Modal } from 'carbon-components-react';
import countryList from '../services/currencies.json';
import {
  useLang,
  LangDefaults,
  determineCountry,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import messages from '../locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { Ending } from './Molecules/Ending/Ending';

/**
 * App component.
 *
 * The top-level parent component that defines this application.
 *
 * @param {object} props Component props.
 * @param {Element} props.element The DOM element that contains this widget.
 */

export interface AppProps {
  element: Element;
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export type que5ValueProps = {
  options: {
    NTQ5A1: {
      value: string;
      checked: boolean;
    };
    NTQ5A2: {
      value: string;
      checked: boolean;
    };
    NTQ5A3: {
      value: string;
      checked: boolean;
    };
    NTQ5A4: {
      value: string;
      checked: boolean;
    };
    NTQ5A5: {
      value: string;
      checked: boolean;
    };
    NTQ5A6: {
      value: string;
      checked: boolean;
    };
  };
};

export type que6ValueProps = {
  options: {
    NTQ6A1: {
      value: string;
      checked: boolean;
    };
    NTQ6A2: {
      value: string;
      checked: boolean;
    };
    NTQ6A3: {
      value: string;
      checked: boolean;
    };
    NTQ6A4: {
      value: string;
      checked: boolean;
    };
    NTQ6A5: {
      value: string;
      checked: boolean;
    };
    NTQ6A6: {
      value: string;
      checked: boolean;
    };
    NTQ6A7: {
      value: string;
      checked: boolean;
    };
    NTQ6A8: {
      value: string;
      checked: boolean;
    };
    NTQ6A9: {
      value: string;
      checked: boolean;
    };
  };
};

export type QuesIDProps = {
  Q1?: string;
  Q2?: string;
  Q3?: string;
  Q4?: string;
  Q5?: string;
  Q6?: string;
  Q7?: string;
};

const WatsonxOrchestrateSelfAssessment = ({
  langCode,
  test,
  countryToTest,
}: AppProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [openLanding, setOpenLanding] = useState(false);
  const { bpName, companyId, logo, desc, mssp } = useBPName(
    UT30WatsonxOSA,
    true,
  );
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [formComplete, setFormComplete] = useState(false);
  const [trackQuesID, setTrackQuesID] = useState<QuesIDProps>({
    Q1: '',
    Q2: '',
    Q3: '',
    Q4: '',
    Q5: '',
    Q6: '',
    Q7: '',
  });
  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [que1Value, setQue1Value] = useState('');
  const [que2Value, setQue2Value] = useState('');
  const [que3Value, setQue3Value] = useState('');
  const [que4Value, setQue4Value] = useState('');
  const [que5Value, setQue5Value] = useState<que5ValueProps>({
    options: {
      NTQ5A1: {
        value: '',
        checked: false,
      },
      NTQ5A2: {
        value: '',
        checked: false,
      },
      NTQ5A3: {
        value: '',
        checked: false,
      },
      NTQ5A4: {
        value: '',
        checked: false,
      },
      NTQ5A5: {
        value: '',
        checked: false,
      },
      NTQ5A6: {
        value: '',
        checked: false,
      },
    },
  });
  const [que6Value, setQue6Value] = useState<que6ValueProps>({
    options: {
      NTQ6A1: {
        value: '',
        checked: false,
      },
      NTQ6A2: {
        value: '',
        checked: false,
      },
      NTQ6A3: {
        value: '',
        checked: false,
      },
      NTQ6A4: {
        value: '',
        checked: false,
      },
      NTQ6A5: {
        value: '',
        checked: false,
      },
      NTQ6A6: {
        value: '',
        checked: false,
      },
      NTQ6A7: {
        value: '',
        checked: false,
      },
      NTQ6A8: {
        value: '',
        checked: false,
      },
      NTQ6A9: {
        value: '',
        checked: false,
      },
    },
  });
  const [que7Value, setQue7Value] = useState('');
  const [model2, setModel2] = useState(false);
  // lang and userlocale is based out of ddo

  /* Have urx form deault in us-en when on language us page */
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );

  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };
  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [pathSelected, setPathSelected] = useState('');
  const [que1Points, setQue1Points] = useState(0);
  const [que2Points, setQue2Points] = useState(0);
  const [que3Points, setQue3Points] = useState(0);
  const [que4Points, setQue4Points] = useState(0);
  const [que5Points, setQue5Points] = useState(0);
  const [que6Points, setQue6Points] = useState(0);
  const [que7Points, setQue7Points] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [resultsTrack, setResultsTrack] = useState('Discovery');
  const [openEnding, SetOpenEnding] = useState(false);
  const [cookieContent, setCookieContent] = useState('');
  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  useEffect(() => {
    setCookieContent(
      'Track selected: ' +
        pathSelected +
        ' | Question1: ' +
        trackQuesID.Q1 +
        ' | Answer1: ' +
        que1Value +
        ' | Q1Points: ' +
        que1Points +
        ' | Question2: ' +
        trackQuesID.Q2 +
        ' | Answer2: ' +
        que2Value +
        ' | Q2Points: ' +
        que2Points +
        ' | Question3: ' +
        trackQuesID.Q3 +
        ' | Answer3: ' +
        que3Value +
        ' | Q3Points: ' +
        que3Points +
        ' | Question4: ' +
        trackQuesID.Q4 +
        ' | Answer4: ' +
        que4Value +
        ' | Q4Points: ' +
        que4Points +
        ' | Question5: ' +
        trackQuesID.Q5 +
        ' | Answer5: ' +
        que5Value.options.NTQ5A1.value +
        ',' +
        que5Value.options.NTQ5A2.value +
        ',' +
        que5Value.options.NTQ5A3.value +
        ',' +
        que5Value.options.NTQ5A4.value +
        ',' +
        que5Value.options.NTQ5A5.value +
        ',' +
        que5Value.options.NTQ5A6.value +
        ' | Q5Points: ' +
        que5Points +
        ' | Question6: ' +
        trackQuesID.Q6 +
        ' | Answer6: ' +
        que6Value.options.NTQ6A1.value +
        ',' +
        que6Value.options.NTQ6A2.value +
        ',' +
        que6Value.options.NTQ6A3.value +
        ',' +
        que6Value.options.NTQ6A4.value +
        ',' +
        que6Value.options.NTQ6A5.value +
        ',' +
        que6Value.options.NTQ6A6.value +
        ',' +
        que6Value.options.NTQ6A7.value +
        ',' +
        que6Value.options.NTQ6A8.value +
        ',' +
        que6Value.options.NTQ6A9.value +
        ' | Q6Points: ' +
        que6Points +
        ' | Question7: ' +
        trackQuesID.Q7 +
        ' | Answer7: ' +
        que7Value +
        ' | Q7Points: ' +
        que7Points +
        ' | Total Points: ' +
        totalPoints +
        ' | Result track: ' +
        resultsTrack +
        (getBp() ? `|Preferred BP:${getBp()}` : ''),
    );
  });
  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };
  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [environment] = useEnv();
  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setOpen(false);
    setDrawerOpen(false);
  };

  useEffect(() => {
    setTotalPoints(
      que1Points +
        que2Points +
        que3Points +
        que4Points +
        que5Points +
        que6Points +
        que7Points,
    );
  }, [
    que1Points,
    que2Points,
    que3Points,
    que4Points,
    que5Points,
    que6Points,
    que7Points,
  ]);

  useEffect(() => {
    if (totalPoints < 19) {
      setResultsTrack('Discovery');
    } else if (totalPoints > 18 && totalPoints < 38) {
      setResultsTrack('Experimentation');
    } else if (totalPoints > 37) {
      setResultsTrack('Adoption');
    }
  }, [totalPoints]);

  const handleUrxForm = () => {
    SetOpenEnding(true);
    setOpen(false);
    setOpenLanding(false);
  };

  const handleLaunchModalLanding = () => {
    setOpenLanding(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Watsonx Orchestrate Self Assessment',
      object: 'WatsonxOrchestrateSelfAssessment page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Assessment',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const handleLaunchModal = () => {
    setOpenLanding(false);
    setOpen(true);
    setStep(1);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Watsonx Orchestrate Self Assessment',
      object: 'WatsonxOrchestrateSelfAssessment page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Assessment',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const handleCloseModal = () => {
    setOpen(false);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Watsonx Orchestrate Self Assessment',
      object: 'Watsonx Orchestrate SA Page',
      resultValue: 'CTA clicked',
      CTA: 'Close',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };

  const handleCloseQuestions = () => {
    setOpen(false);
    setOpenLanding(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Watsonx Orchestrate Self Assessment',
      object: 'Watsonx Orchestrate SA Page',
      resultValue: 'CTA clicked',
      CTA: 'Back on first question',
      location: 'Assessment',
      action: '',
      field: '',
    });
  };
  const reset = () => {
    setQue1Value('');
    setQue2Value('');
    setQue3Value('');
    setQue4Value('');
    setQue5Value({
      options: {
        NTQ5A1: {
          value: '',
          checked: false,
        },
        NTQ5A2: {
          value: '',
          checked: false,
        },
        NTQ5A3: {
          value: '',
          checked: false,
        },
        NTQ5A4: {
          value: '',
          checked: false,
        },
        NTQ5A5: {
          value: '',
          checked: false,
        },
        NTQ5A6: {
          value: '',
          checked: false,
        },
      },
    });
    setQue6Value({
      options: {
        NTQ6A1: {
          value: '',
          checked: false,
        },
        NTQ6A2: {
          value: '',
          checked: false,
        },
        NTQ6A3: {
          value: '',
          checked: false,
        },
        NTQ6A4: {
          value: '',
          checked: false,
        },
        NTQ6A5: {
          value: '',
          checked: false,
        },
        NTQ6A6: {
          value: '',
          checked: false,
        },
        NTQ6A7: {
          value: '',
          checked: false,
        },
        NTQ6A8: {
          value: '',
          checked: false,
        },
        NTQ6A9: {
          value: '',
          checked: false,
        },
      },
    });
    setQue7Value('');
    setQue1Points(0);
    setQue2Points(0);
    setQue3Points(0);
    setQue4Points(0);
    setQue5Points(0);
    setQue6Points(0);
    setQue7Points(0);
    setTotalPoints(0);
    setTrackQuesID({ Q1: '', Q2: '', Q3: '', Q4: '', Q5: '', Q6: '', Q7: '' });
    setStep(1);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'Watsonx Orchestrate Self Assessment',
      object: 'Watsonx Orchestrate SA Page',
      resultValue: 'CTA clicked',
      CTA: 'Star Over',
      location: 'Assessment',
      action: '',
      field: '',
    });
    setTotalPoints(0);
  };

  const reset1 = () => {
    reset();
    SetOpenEnding(false);
    setOpenLanding(false);
    setOpen(true);
  };

  const reset2 = () => {
    reset1();
    setOpen(false);
  };

  return (
    <div className="watsonxsa_widget widget-styles">
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      {!open && !openLanding && (
        <Welcome
          handleLaunchModal={handleLaunchModalLanding}
          model2={model2}
          setModel2={setModel2}
          modalBtnLangText={modalLocalLanguage}
          localExist={localExist}
          toggleCountry={toggleCountry}
          flag={false} // to enable/disable globalization
        />
      )}
      {!open && openLanding && (
        <Landing
          setPathSelected={setPathSelected}
          setOpenLanding={setOpenLanding}
          handleLaunchModal={handleLaunchModal}
        ></Landing>
      )}
      {open && !openLanding && (
        <Assessment
          open={open}
          langCode={langCode}
          bpName={bpName}
          logo={logo}
          companyId={companyId}
          handleCloseModal={handleCloseModal}
          handleCloseQuestions={handleCloseQuestions}
          step={step}
          setStep={setStep}
          setOpen={setOpen}
          que1Value={que1Value}
          setQue1Value={setQue1Value}
          que2Value={que2Value}
          setQue2Value={setQue2Value}
          que3Value={que3Value}
          setQue3Value={setQue3Value}
          que4Value={que4Value}
          setQue4Value={setQue4Value}
          que5Value={que5Value}
          setQue5Value={setQue5Value}
          que6Value={que6Value}
          setQue6Value={setQue6Value}
          que7Value={que7Value}
          setQue7Value={setQue7Value}
          setTrackQuesID={setTrackQuesID}
          trackQuesID={trackQuesID}
          localeCode={urxlang}
          reset={reset}
          formComplete={formComplete}
          setFormComplete={setFormComplete}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          handleUrxForm={handleUrxForm}
          que1Points={que1Points}
          setQue1Points={setQue1Points}
          que2Points={que2Points}
          setQue2Points={setQue2Points}
          que3Points={que3Points}
          setQue3Points={setQue3Points}
          que4Points={que4Points}
          setQue4Points={setQue4Points}
          que5Points={que5Points}
          setQue5Points={setQue5Points}
          que6Points={que6Points}
          setQue6Points={setQue6Points}
          que7Points={que7Points}
          setQue7Points={setQue7Points}
          totalPoints={totalPoints}
          setTotalPoints={setTotalPoints}
          pathSelected={pathSelected}
        />
      )}
      <div className="bx--row">
        <div
          className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
          id="countryModal"
        >
          <Modal
            open={model2}
            size="sm"
            preventCloseOnClickOutside={true}
            onRequestClose={() => {
              setModel2(false);
            }}
            modalLabel={`${getMessage(
              messages.foundCountry,
            )} ${modalLocalCountry}. ${getMessage(
              messages.accessPageCapitalized,
            )} ${modalLocalLanguage} (${modalLocalCurrency})`}
            modalHeading={`${getMessage(
              messages.availableIn,
            )} ${modalLocalLanguage} ${getMessage(
              messages.localCurrency_text,
            )} ${modalLocalCurrency}`}
            secondaryButtonText={getMessage(messages.stayHere)}
            primaryButtonText={getMessage(messages.takeMeThere)}
            onRequestSubmit={() => {
              localStorage.setItem('langLocale', selectedLocale);
              (window.location.href =
                'https://www.ibm.com/' +
                selectedLocale +
                '/products/watsonx/pricing'),
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'Watsonx Orchestrate Self Assessment',
                  object: 'Watsonx Orchestrate SA Page',
                  resultValue: 'CTA clicked',
                  CTA: 'Switched to country ' + modalLocalCountry,
                  location: 'Assessment',
                  action: '',
                  field: '',
                });
              return true;
            }}
            onSecondarySubmit={() => {
              segmentsTracking('CTA Clicked', {
                pageTitle: 'Watsonx Orchestrate Self Assessment',
                object: 'Watsonx Orchestrate SA Page',
                resultValue: 'CTA clicked',
                CTA: 'Stay here button clicked ' + modalLocalCountry,
                location: 'Assessment',
                action: '',
                field: '',
              });
              setToggleCountry(false);
              setModel2(false);
              return true;
            }}
          ></Modal>
        </div>
      </div>
      {openEnding && (
        <Ending
          langCode={langCode}
          bpName={bpName}
          logo={logo}
          companyId={companyId}
          totalPoints={totalPoints}
          selectedPath={pathSelected}
          reset1={reset1}
          reset2={reset2}
          cookieContent={cookieContent}
        ></Ending>
      )}
    </div>
  );
};

export default WatsonxOrchestrateSelfAssessment;
